/**
 * SharedTripPageNew — shared trip detail page with full trip data display.
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useViewShareLink } from '../hooks/useViewShareLink';
import { useAcceptShareLink } from '../hooks/useAcceptShareLink';
import { useAuth } from '../contexts/AuthContext';
import { DataCard } from '../design-system/primitives/DataCard';
import { DataCardRow } from '../design-system/primitives/DataCardRow';
import { SkeletonRow } from '../design-system/primitives/SkeletonRow';
import { EmptyState } from '../components/shared/EmptyState';
import { Share2, Calendar, CheckCircle } from 'lucide-react';

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SharedTripPageNew(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: sharedTrip, isLoading: tripLoading } = useViewShareLink(token ?? '');
  const acceptMutation = useAcceptShareLink();
  const trip = sharedTrip;
  const isLoading = tripLoading || authLoading;

  const handleViewTrip = () => {
    if (!trip) return;
    acceptMutation.mutate(
      { tripId: trip.tripId, token: trip.token },
      {
        onSuccess: () => {
          navigate(`/trip/${trip.tripId}`);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Shared Trip</h1>
      {isLoading ? (
        <SkeletonRow count={4} width="100%" />
      ) : trip ? (
        <div className="max-w-3xl mx-auto space-y-4">
          <DataCard title={trip.tripName} subtitle={`${formatDate(trip.startDate)} — ${formatDate(trip.endDate)}`}>
            <DataCardRow label="Visits" value={`${trip.visitCount} park visits`} />
          </DataCard>
          {isAuthenticated ? (
            <button
              type="button"
              disabled={acceptMutation.isPending}
              onClick={handleViewTrip}
              className="w-full flex items-center justify-center gap-2 bg-disney-blue-600 hover:bg-disney-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              {acceptMutation.isPending ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              View Trip
            </button>
          ) : (
            <div className="bg-white dark:bg-surface-dark-card rounded-lg p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-disney-blue-500" />
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Trip Visits</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Trip shared via link. Sign in to view visit details and manage your trip.
              </p>
            </div>
          )}
        </div>
      ) : (
        <EmptyState icon={Share2} iconColor="text-gray-400" title="Trip not found" subtitle="The shared trip may have been removed." />
      )}
    </div>
  );
}
